import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, InputNumber, Input } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
import { receivePurchaseOrderApi, getPurchaseOrderDocumentApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import DocumentAttachment from '../../components/DocumentAttachment';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Partial/full stock receiving against an OPEN or PARTIALLY_RECEIVED purchase order.
 *  Row quantities/remarks are kept in a { [po_line_id]: { quantity, remarks } } map
 *  (same pattern as StockTakeDetail's edited-item map). */
const ReceiveStockModal = ({ open, onClose, po, onReceived }) => {
  const { mainWarehouseOptions, warehousesLoading, warehousesError, fetchWarehouses } = useUniformMgmt();

  const [warehouse, setWarehouse] = useState(null);
  const [receivedDate, setReceivedDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [rowState, setRowState] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const receivableLines = useMemo(
    () => (po?.lines || []).filter((l) => l.remaining_quantity > 0),
    [po]
  );

  useEffect(() => {
    if (!open || !po) return;
    setWarehouse(null);
    setReceivedDate('');
    setRemarks('');
    setInvoiceNumber(po.invoice_number || '');
    setInvoiceDate(po.invoice_date || '');
    setInvoiceDoc(po.invoice_document_url
      ? { document_url: po.invoice_document_url, document_name: po.invoice_document_name }
      : null);
    const initial = {};
    receivableLines.forEach((l) => { initial[l.id] = { quantity: 0, remarks: '' }; });
    setRowState(initial);
    // receivableLines is derived from po via useMemo; re-running on po change is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, po]);

  // Auto-pick the receiving warehouse when there's only one active main
  // warehouse to choose from. Runs after the reset effect above (which
  // clears `warehouse` to null on every open), so a single-option company
  // never makes the user pick. Also reconciles the other direction: if the
  // picked warehouse drops out of the list (deactivated / stripped of the
  // role elsewhere), clear it rather than leave a now-invalid warehouse
  // selected and submittable.
  useEffect(() => {
    if (!open) return;
    if (mainWarehouseOptions.length === 1) {
      setWarehouse((prev) => (
        prev && prev.value === mainWarehouseOptions[0].value ? prev : mainWarehouseOptions[0]
      ));
    } else if (warehouse && !mainWarehouseOptions.some((o) => o.value === warehouse.value)) {
      setWarehouse(null);
    }
  }, [open, mainWarehouseOptions, warehouse]);

  const updateRow = (lineId, patch) => {
    setRowState((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }));
  };

  const handleReceiveAllBalance = () => {
    setRowState((prev) => {
      const next = { ...prev };
      receivableLines.forEach((l) => {
        next[l.id] = { ...next[l.id], quantity: l.remaining_quantity };
      });
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (mainWarehouseOptions.length === 0 && !warehousesLoading) {
      toast.error(
        warehousesError ? "Couldn't load warehouses — retry" : 'No main warehouse configured — flag one under Masters → Warehouses',
        toastOpts
      );
      return;
    }
    if (!warehouse) { toast.error('Warehouse is required', toastOpts); return; }
    if (!receivedDate) { toast.error('Received date is required', toastOpts); return; }

    const lines = receivableLines
      .map((l) => ({
        po_line: l.id,
        quantity: Number(rowState[l.id]?.quantity || 0),
        remarks: rowState[l.id]?.remarks || '',
      }))
      .filter((l) => l.quantity > 0);

    if (lines.length === 0) {
      toast.error('Enter a quantity for at least one line', toastOpts);
      return;
    }

    setSubmitting(true);
    const payload = {
      warehouse: warehouse.value,
      received_date: receivedDate,
      remarks: remarks || '',
      invoice_number: invoiceNumber || '',
      invoice_date: invoiceDate || null,
      invoice_document_url: invoiceDoc?.document_url || '',
      invoice_document_name: invoiceDoc?.document_name || '',
      lines,
    };

    receivePurchaseOrderApi(po.id, payload).then((response) => {
      setSubmitting(false);
      const reference = response.data?.data?.reference;
      toast.success(reference ? `Received ${reference}` : 'Stock received', toastOpts);
      onReceived();
    }).catch((e) => {
      setSubmitting(false);
      const err = e.response?.data?.error;
      if (Array.isArray(err)) {
        err.slice(0, 5).forEach((msg) => toast.error(msg, toastOpts));
      } else {
        toast.error(uniformErrorMessage(e, 'Failed to receive stock'), toastOpts);
      }
    });
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    // Pinned: seven columns in a slide-over, and the receiver types into
    // "Receive Now" at the far right — the item name has to stay visible.
    { title: 'Item', dataIndex: 'uniform_item_name', ...pinLeft(190, { maxWidth: 200 }) },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '-' },
    { title: 'Ordered', dataIndex: 'ordered_quantity' },
    { title: 'Received so far', dataIndex: 'received_quantity' },
    { title: 'Balance', dataIndex: 'remaining_quantity', render: (v) => <strong>{v}</strong> },
    {
      title: 'Receive Now',
      render: (_, record) => (
        <InputNumber min={0} max={record.remaining_quantity}
          value={rowState[record.id]?.quantity ?? 0}
          onChange={(val) => updateRow(record.id, { quantity: val ?? 0 })}
          style={{ width: 90 }} />
      )
    },
    {
      title: 'Remarks',
      render: (_, record) => (
        <Input value={rowState[record.id]?.remarks ?? ''}
          onChange={(e) => updateRow(record.id, { remarks: e.target.value })}
          placeholder="Remarks" style={{ minWidth: 140 }} />
      )
    },
  ];

  if (!po) return null;

  // The Lines table grows with the PO, so without a bounded body a large PO
  // pushes the dialog past the viewport and scrolls its own title and the
  // Receive Stock button out of reach. Scrolling the body instead keeps both
  // pinned. Uses `styles.body` rather than the `bodyStyle` the older modals in
  // this module use — antd 5.29 deprecates that alias (modal/Modal.js:100).
  return (
    <Modal title={`Receive Stock — PO ${po.po_number}`} open={open} onCancel={handleClose}
      width={960} centered destroyOnHidden
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      footer={[
        <button key="cancel" type="button" className="btn btn-light" onClick={handleClose} disabled={submitting}>
          Cancel
        </button>,
        <Button key="submit" label="Receive Stock" className="btn btn-primary ms-2"
          loading={submitting} disabled={submitting} onClick={handleSubmit} />
      ]}>
      <div className="row">
        <div className="col-md-4">
          <div className="input-block mb-3">
            <label className="col-form-label">Receive Into Warehouse <span className="text-danger">*</span></label>
            {mainWarehouseOptions.length === 0 && !warehousesLoading ? (
              <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                {warehousesError ? (
                  <>
                    Couldn't load warehouses —{' '}
                    <button type="button" className="btn btn-link p-0 align-baseline" onClick={fetchWarehouses}>
                      retry
                    </button>.
                  </>
                ) : (
                  'No main warehouse configured — flag one under Masters → Warehouses.'
                )}
              </div>
            ) : (
              // Not isClearable: with exactly one option the reconcile effect
              // above re-selects it on the very next render, so the X is a
              // no-op that only flickers the field before it re-fills.
              <FilterSelect onChange={setWarehouse} optionLabel="label" optionValue="value"
                options={mainWarehouseOptions} value={warehouse} placeholder="Select Warehouse" isLoading={warehousesLoading} />
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="input-block mb-3">
            <label className="col-form-label" htmlFor="receive-received-date">Received Date <span className="text-danger">*</span></label>
            <DateField id="receive-received-date" value={receivedDate} onChange={setReceivedDate}
              placeholder="Select received date" />
          </div>
        </div>
        <div className="col-md-4">
          <div className="input-block mb-3">
            <label className="col-form-label">Remarks</label>
            <textarea className="form-control" rows={1} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks" />
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header"><h6 className="mb-0">Invoice (optional)</h6></div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <label className="col-form-label">Invoice Number</label>
              <input className="form-control" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="col-form-label" htmlFor="receive-invoice-date">Invoice Date</label>
              <DateField id="receive-invoice-date" value={invoiceDate} onChange={setInvoiceDate}
                placeholder="Select invoice date" />
            </div>
            <div className="col-md-6">
              <DocumentAttachment label="Invoice Document" value={invoiceDoc} onChange={setInvoiceDoc}
                fetchDocumentApi={(p) => getPurchaseOrderDocumentApi(po.id, 'invoice', p)} />
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Lines</h6>
        <Button type="button" label="Receive All Balance" className="btn btn-outline-primary btn-sm"
          onClick={handleReceiveAllBalance} disabled={receivableLines.length === 0} />
      </div>
      {/* `size="small"` to match the other in-form line grids: the compact
          density scale (`cellPaddingBlockSM`/`cellPaddingInlineSM`) keeps a
          long receive list inside the dialog without scrolling the panel.
          Same skin — only the padding changes. */}
      <Table columns={columns} dataSource={receivableLines} rowKey={(r) => r.id}
        size="small" pagination={false} scroll={{ x: 'max-content' }} />
    </Modal>
  );
};

export default ReceiveStockModal;
